import { useCreateTaskMutation } from '@/hooks/use-create-task';
import { useDealByShortId } from '@/hooks/use-deals-selectors';
import {
  FEATURE_FLAG_NAMES,
  useIsFeatureEnabled,
} from '@/hooks/use-feature-flags';
import { AIDealSuggestion, AITaskSuggestion, Deal } from '@/models/registry';
import { aiDealSuggestions as aiSuggestionStore } from '@/models/stores/ai/deal-suggestions';
import { aiTaskSuggestions } from '@/models/stores/ai/task-suggestions';
import { dealStore } from '@/models/stores/deals';
import type { CoreDeal } from '@/models/types';
import { myTasksCopilotStore } from '@/stores/my-tasks-copilot-store';
import { observer } from 'mobx-react-lite';
import { createContext, useContext, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useDealContext } from './deal-provider';

export interface AISuggestionsContextType {
  allSuggestions: AIDealSuggestion[];
  allTaskSuggestions: AITaskSuggestion[];
  activeSuggestion: AIDealSuggestion | null;
  acceptSuggestion: (suggestionId: string) => void;
  dismissSuggestion: (suggestionId: string) => void;
  acceptTaskSuggestion: (suggestionId: string) => void;
  dismissTaskSuggestion: (suggestionId: string) => void;
  getSuggestionsByField: (
    dealId: string,
    field: keyof CoreDeal,
  ) => AIDealSuggestion[];
  getTaskSuggestionsByDealId: (dealId: string) => AITaskSuggestion[];
  areSuggestionsLoading: boolean;
  generateCopilotOutput: () => void;
}

const AISuggestionsContext = createContext<
  AISuggestionsContextType | undefined
>(undefined);

export const AISuggestionsProvider: React.FC<{
  children: React.ReactNode;
}> = observer(({ children }) => {
  const { currentShortId } = useDealContext();
  const deal = useDealByShortId(currentShortId);

  const { isFeatureEnabled: isAISuggestionsEnabled } = useIsFeatureEnabled({
    featureFlagName: FEATURE_FLAG_NAMES.AI_SUGGESTIONS,
  });

  const allSuggestions = aiSuggestionStore.all;
  const allTaskSuggestions = aiTaskSuggestions.all;
  const areSuggestionsLoading = aiSuggestionStore.areSuggestionsLoading;

  // for now, the active suggestion is just whatever is next in the list
  const activeSuggestion = useMemo(() => {
    return allSuggestions[0] || null;
  }, [allSuggestions]);

  const generateCopilotOutput = () => {
    if (!isAISuggestionsEnabled) {
      return;
    }

    // Double-check copilot isn't active when timer fires
    if (myTasksCopilotStore.isActive) {
      return;
    }

    // DEMO NOTE: clear suggestions when time-based trigger fires
    aiSuggestionStore.clear();
    aiTaskSuggestions.clear();
    aiSuggestionStore.generateSuggestionsForDealV2({
      deal: deal as Deal,
    });
  };

  const getSuggestionsByField = (dealId: string, field: keyof CoreDeal) => {
    return aiSuggestionStore.getSuggestionsByDealAndField(dealId, field);
  };

  const getTaskSuggestionsByDealId = (dealId: string) => {
    return aiTaskSuggestions.getSuggestionsByDealId(dealId);
  };

  const acceptSuggestion = (id: string) => {
    const suggestion = aiSuggestionStore.getById(id);
    if (suggestion) {
      switch (suggestion.field) {
        case 'status':
          dealStore.updateStatus({
            id: suggestion.deal_id,
            status: suggestion.suggested_value as CoreDeal['status'],
          });
          break;
        case 'stage':
          dealStore.updateStage({
            id: suggestion.deal_id,
            stage: suggestion.suggested_value as CoreDeal['stage'],
          });
          break;
        case 'priority':
          dealStore.updatePriority({
            id: suggestion.deal_id,
            priority: suggestion.suggested_value as CoreDeal['priority'],
          });
          break;
        default:
          console.log('unhandled field', suggestion.field);
      }
      aiSuggestionStore.saveAISuggestionDecision(id, 'accepted');
      aiSuggestionStore.remove(id, suggestion.deal_id, suggestion.field);
    }
  };

  const dismissSuggestion = (id: string) => {
    const suggestion = aiSuggestionStore.getById(id);
    if (suggestion) {
      aiSuggestionStore.saveAISuggestionDecision(id, 'dismissed');
      aiSuggestionStore.remove(id, suggestion.deal_id, suggestion.field);
    }
  };

  const createTaskMutation = useCreateTaskMutation();

  const acceptTaskSuggestion = (id: string) => {
    const suggestion = aiTaskSuggestions.getById(id);
    if (suggestion) {
      // Remove the suggestion immediately (optimistic update)
      aiTaskSuggestions.remove(id, suggestion.deal_id);

      // Create the task using the same mutation as deal-task-selector
      const taskId = uuidv4();
      createTaskMutation.mutate({
        task: {
          id: taskId,
          title: suggestion.title,
          description: suggestion.description,
        },
        dealId: suggestion.deal_id,
      });
    }
  };

  const dismissTaskSuggestion = (id: string) => {
    const suggestion = aiTaskSuggestions.getById(id);
    if (suggestion) {
      // aiTaskSuggestions.saveAITaskSuggestionDecision(id, 'dismissed');
      aiTaskSuggestions.remove(id, suggestion.deal_id);
    }
  };

  return (
    <AISuggestionsContext.Provider
      value={{
        allSuggestions,
        allTaskSuggestions,
        activeSuggestion,
        acceptSuggestion,
        dismissSuggestion,
        acceptTaskSuggestion,
        dismissTaskSuggestion,
        getSuggestionsByField,
        getTaskSuggestionsByDealId,
        areSuggestionsLoading,
        generateCopilotOutput,
      }}
    >
      {children}
    </AISuggestionsContext.Provider>
  );
});

export const useAISuggestionsContext = () => {
  const context = useContext(AISuggestionsContext);
  if (!context) {
    throw new Error(
      'useAISuggestionsContext must be used within a AISuggestionsProvider',
    );
  }
  return context;
};
